import { ReactElement, useMemo, useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import Skeleton from './Skeleton';
import ChipSelector from './ChipSelector';
import { getChannel } from 'utils/channelNamesGridded';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';
import { useUpdateRun } from 'hooks/useRuns';

interface PlotterProps {
  availableFields: { label: string; field: string }[];
  data: Record<string, any> | null;
  title?: string;
  storageKey: string;
  yAxisTitle: string;
  yAxisUnit: string; // Added prop
  unitScalar?: number; // Added prop
  // New props for secondary y-axis
  availableFieldsSecondary?: { label: string; field: string }[];
  yAxisTitleRight?: string;
  yAxisUnitRight?: string;
  unitScalarSecondary?: number;
  calibration?: any;
  segmentTimestamps?: number[]; // Array of timestamps for segment dividers
  excludeCandidates?: number[]; // Array of all possible lap values from exclude_candidates_ts
  excludeTs?: number[]; // Array of currently excluded timestamps
  runId?: string; // Run ID for updating
  defaultFields?: string[]; // Fields to select by default when no localStorage state exists
  hideChipSelector?: boolean; // When true, skip rendering ChipSelector and plot all passed fields
  hideLapSelector?: boolean; // When true, hide the Raw Lap Selection control (annotations stay)
}

// Safe min/max functions that don't exceed call stack for large arrays
const safeMax = (arr: number[]): number => {
  if (arr.length === 0) return -Infinity;
  return arr.reduce((max, val) => (val > max ? val : max), arr[0]);
};

const safeMin = (arr: number[]): number => {
  if (arr.length === 0) return Infinity;
  return arr.reduce((min, val) => (val < min ? val : min), arr[0]);
};

// Color palette for consistent field colors
const COLOR_PALETTE = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F43F5E', // Rose
];

// Function to get consistent color for a field
const getFieldColor = (fieldName: string, allFields: { field: string; isSecondary: boolean }[]): string => {
  const fieldIndex = allFields.findIndex((field) => field.field === fieldName);
  return COLOR_PALETTE[fieldIndex % COLOR_PALETTE.length];
};

export default function Plotter({
  isPending,
  title,
  ...otherProps
}: PlotterProps & { isPending: boolean }): ReactElement {
  return (
    <div className="cr-space-y-6">
      <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
        <h2 className="cr-mb-4 cr-text-xl cr-font-semibold">{title}</h2>
        <Skeleton isPending={isPending}>
          <PlotterContent {...otherProps} />
        </Skeleton>
      </div>
    </div>
  );
}

export function PlotterContent(props: PlotterProps): ReactElement {
  const {
    availableFields,
    data,
    title,
    storageKey,
    yAxisTitle,
    yAxisUnit,
    unitScalar = 1,
    availableFieldsSecondary,
    yAxisTitleRight,
    yAxisUnitRight,
    unitScalarSecondary = 1,
    calibration,
    segmentTimestamps,
    excludeCandidates,
    excludeTs,
    runId,
    defaultFields,
    hideChipSelector,
    hideLapSelector,
  } = props;

  // Combine all available fields for unified selection
  const allAvailableFields = useMemo(() => {
    const primaryFields = availableFields.map((field) => ({ ...field, isSecondary: false }));
    const secondaryFields = availableFieldsSecondary?.map((field) => ({ ...field, isSecondary: true })) || [];
    return [...primaryFields, ...secondaryFields];
  }, [availableFields, availableFieldsSecondary]);

  // Selection state is managed here
  const getInitialState = (): { [key: string]: boolean } => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const hasAllOptions = allAvailableFields.every((option) => parsed.hasOwnProperty.call(parsed, option.field));
        if (hasAllOptions) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error reading state from localStorage:', e);
    }
    if (defaultFields) {
      return allAvailableFields.reduce(
        (acc, option) => ({ ...acc, [option.field]: defaultFields.includes(option.field) }),
        {} as { [key: string]: boolean },
      );
    }
    return allAvailableFields.reduce(
      (acc, option) => ({ ...acc, [option.field]: true }),
      {} as { [key: string]: boolean },
    );
  };

  const [selectedFields, setSelectedFields] = useState<{ [key: string]: boolean }>(getInitialState);

  // When hideChipSelector is true, keep all passed fields selected as availableFields changes
  useEffect(() => {
    if (hideChipSelector) {
      const allSelected = allAvailableFields.reduce(
        (acc, option) => ({ ...acc, [option.field]: true }),
        {} as { [key: string]: boolean },
      );
      setSelectedFields(allSelected);
    }
  }, [hideChipSelector, allAvailableFields]);

  const [isLapSectionOpen, setIsLapSectionOpen] = useState(false);
  const [selectedLaps, setSelectedLaps] = useState<{ [key: number]: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [localExcludeTs, setLocalExcludeTs] = useState<number[] | undefined>(excludeTs);

  const updateRunMutation = useUpdateRun();

  // Update local exclude_ts when prop changes
  useEffect(() => {
    setLocalExcludeTs(excludeTs);
  }, [excludeTs]);

  const handleSelectionChange = (newState: { [key: string]: boolean }) => {
    setSelectedFields(newState);
  };

  // Generate sequential lap numbers based on exclude_candidates_ts length
  const lapNumbers = useMemo(() => {
    if (excludeCandidates && excludeCandidates.length > 0) {
      // Generate sequential numbers from 1 to length
      return Array.from({ length: excludeCandidates.length }, (_, i) => i + 1);
    }
    return [];
  }, [excludeCandidates]);

  // Initialize selected laps based on exclude_ts
  useEffect(() => {
    if (lapNumbers.length > 0 && excludeCandidates) {
      const initialLaps: { [key: number]: boolean } = {};

      // For each lap number (1, 2, 3, etc.)
      for (let i = 0; i < lapNumbers.length; i++) {
        const lapNumber = lapNumbers[i]; // This is 1, 2, 3, etc.
        const candidateValue = excludeCandidates[i]; // The corresponding value from exclude_candidates_ts

        // Check if this value is in exclude_ts (if it is, checkbox should be unchecked)
        const isExcluded = localExcludeTs?.includes(candidateValue) || false;
        initialLaps[lapNumber] = !isExcluded; // Checkbox is checked if NOT excluded
      }

      setSelectedLaps(initialLaps);
    }
  }, [lapNumbers, excludeCandidates, localExcludeTs]);

  const handleLapToggle = (lapNumber: number) => {
    setSelectedLaps((prev) => ({ ...prev, [lapNumber]: !prev[lapNumber] }));
  };

  const handleSaveLapSelection = async () => {
    if (!runId || !excludeCandidates) return;

    setIsSaving(true);

    // Create array of excluded values (unchecked checkboxes)
    const excludedValues = [];
    for (let i = 0; i < lapNumbers.length; i++) {
      const lapNumber = lapNumbers[i];
      if (!selectedLaps[lapNumber]) {
        // Add the corresponding value from exclude_candidates_ts
        excludedValues.push(excludeCandidates[i]);
      }
    }

    try {
      await updateRunMutation.mutateAsync({
        runId: runId,
        id: runId,
        exclude_ts: excludedValues,
      });

      // Update local state to immediately reflect the changes in shading
      setLocalExcludeTs(excludedValues);

      // Optionally show success message
    } catch (error) {
      console.error('Failed to save lap selection:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Compute selected secondary fields outside chartData for reuse
  const selectedSecondaryFields = useMemo(
    () =>
      allAvailableFields
        .filter((field) => field.isSecondary && selectedFields[field.field])
        .map((field) => field.field),
    [allAvailableFields, selectedFields],
  );

  const chartData = useMemo(() => {
    if (!data) return null;
    const timeData = data.TimeRel_tG00 || [];

    // Primary series
    const selectedPrimaryFields = allAvailableFields
      .filter((field) => !field.isSecondary && selectedFields[field.field])
      .map((field) => field.field);

    const primarySeries = selectedPrimaryFields.map((fieldName) => {
      const fieldData = getChannel(data, fieldName, calibration) || [];
      const validDataPoints = timeData
        .map((time: number, index: number) => ({
          x: time,
          y: fieldData[index] * unitScalar,
        }))
        .filter(
          (point: { x: number; y: number }) => point.x != null && point.y != null && !isNaN(point.x) && !isNaN(point.y),
        );
      return {
        name: availableFields.find((field) => field.field === fieldName)?.label || fieldName,
        data: validDataPoints,
      };
    });

    // Secondary series (if available)
    const secondarySeries = selectedSecondaryFields.map((fieldName) => {
      const fieldData = getChannel(data, fieldName, calibration) || [];
      const validDataPoints = timeData
        .map((time: number, index: number) => ({
          x: time,
          y: fieldData[index] * unitScalarSecondary,
        }))
        .filter(
          (point: { x: number; y: number }) => point.x != null && point.y != null && !isNaN(point.x) && !isNaN(point.y),
        );
      return {
        name: availableFieldsSecondary?.find((field) => field.field === fieldName)?.label || fieldName,
        data: validDataPoints,
      };
    });

    return [...primarySeries, ...secondarySeries];
  }, [
    data,
    selectedFields,
    allAvailableFields,
    availableFields,
    availableFieldsSecondary,
    unitScalar,
    unitScalarSecondary,
    selectedSecondaryFields, // add as dependency
  ]);

  // Get the names of all primary and secondary series for yaxis mapping
  const primarySeriesNames = useMemo(() => {
    if (!availableFields) return [];
    return allAvailableFields
      .filter((field) => !field.isSecondary && selectedFields[field.field])
      .map((field) => availableFields.find((f) => f.field === field.field)?.label || field.field);
  }, [allAvailableFields, selectedFields, availableFields]);

  const secondarySeriesNames = useMemo(() => {
    if (!availableFieldsSecondary) return [];
    return selectedSecondaryFields.map(
      (fieldName) => availableFieldsSecondary.find((field) => field.field === fieldName)?.label || fieldName,
    );
  }, [selectedSecondaryFields, availableFieldsSecondary]);

  // Generate colors array based on selected fields in order
  const chartColors = useMemo(() => {
    const selectedPrimaryFields = allAvailableFields
      .filter((field) => !field.isSecondary && selectedFields[field.field])
      .map((field) => field.field);

    const selectedSecondaryFields = allAvailableFields
      .filter((field) => field.isSecondary && selectedFields[field.field])
      .map((field) => field.field);

    const allSelectedFields = [...selectedPrimaryFields, ...selectedSecondaryFields];

    return allSelectedFields.map((fieldName) => getFieldColor(fieldName, allAvailableFields));
  }, [selectedFields, allAvailableFields]);

  const chartOptions = useMemo(() => {
    // Pre-compute min/max X values once to avoid repeated expensive calculations
    const globalXValues = chartData?.flatMap((series) => series.data.map((point: any) => point.x)) ?? [];
    const globalMinX = globalXValues.length > 0 ? safeMin(globalXValues) : 0;
    const globalMaxX = globalXValues.length > 0 ? safeMax(globalXValues) : 0;

    // Build yaxis array: one entry per series
    const yaxis: any[] = [];
    // Primary series yaxes
    if (primarySeriesNames.length > 0) {
      // First primary yaxis: visible
      yaxis.push({
        seriesName: primarySeriesNames[0],
        title: {
          text: yAxisTitle,
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: number) => `${value.toFixed(1)} ${yAxisUnit}`,
        },
      });
      // Additional primary yaxes: hidden but mapped
      for (let i = 1; i < primarySeriesNames.length; i++) {
        yaxis.push({
          seriesName: primarySeriesNames[0],
          show: false,
        });
      }
    }
    // Secondary series yaxes
    if (secondarySeriesNames.length > 0 && yAxisTitleRight) {
      // First secondary yaxis: visible, right side
      yaxis.push({
        seriesName: secondarySeriesNames[0],
        opposite: true,
        title: {
          text: yAxisTitleRight,
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: number) => `${value.toFixed(1)} ${yAxisUnitRight || ''}`,
        },
      });
      // Additional secondary yaxes: hidden but mapped, right side
      for (let i = 1; i < secondarySeriesNames.length; i++) {
        yaxis.push({
          seriesName: secondarySeriesNames[0],
          opposite: true,
          show: false,
        });
      }
    }
    const numPrimarySeries = primarySeriesNames.length;

    // Create vertical line annotations for segment timestamps
    const xAxisAnnotations =
      segmentTimestamps?.map((timestamp) => ({
        x: timestamp,
        strokeDashArray: 0,
        borderColor: '#999999',
        borderWidth: 1,
        opacity: 0.5,
      })) || [];

    // Create segment number annotations (text between lines)
    const segmentLabelAnnotations = [];
    if (segmentTimestamps && segmentTimestamps.length > 0 && chartData && chartData.length > 0) {
      const maxTime = globalMaxX;
      const minTime = globalMinX;

      const segments: { x1: number; x2: number; rawLap: number }[] = [];
      if (segmentTimestamps[0] > minTime) {
        segments.push({ x1: minTime, x2: segmentTimestamps[0], rawLap: 1 });
      }
      for (let i = 0; i < segmentTimestamps.length - 1; i++) {
        segments.push({
          x1: segmentTimestamps[i],
          x2: segmentTimestamps[i + 1],
          rawLap: i + 2,
        });
      }
      const lastTs = segmentTimestamps[segmentTimestamps.length - 1];
      if (maxTime > lastTs) {
        segments.push({ x1: lastTs, x2: maxTime, rawLap: segmentTimestamps.length + 1 });
      }

      const gateChannel: number[] | undefined = data?.Gate_NG10;
      const timeChannel: number[] | undefined = data?.TimeRel_tG00;
      const getTraverseLap = (x1: number, x2: number): number | null => {
        if (!gateChannel || !timeChannel || timeChannel.length === 0) return null;
        let lo = 0;
        let hi = timeChannel.length - 1;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (timeChannel[mid] < x1) lo = mid + 1;
          else hi = mid;
        }
        let sum = 0;
        let count = 0;
        for (let i = lo; i < timeChannel.length && timeChannel[i] <= x2; i++) {
          sum += gateChannel[i];
          count++;
        }
        if (count === 0) return null;
        return Math.round(sum / count);
      };

      segments.forEach((seg) => {
        const midpoint = (seg.x1 + seg.x2) / 2;
        const candidateValue = excludeCandidates?.[seg.rawLap - 1];
        const isExcluded =
          candidateValue !== undefined && (localExcludeTs?.includes(candidateValue) || false);
        const traverseLap = isExcluded ? null : getTraverseLap(seg.x1, seg.x2);

        // Raw lap number — faded, positioned at top of plot
        segmentLabelAnnotations.push({
          x: midpoint,
          strokeDashArray: 0,
          borderColor: 'transparent',
          label: {
            text: String(seg.rawLap),
            orientation: 'horizontal',
            position: 'top',
            offsetX: -14,
            offsetY: 8,
            style: {
              fontSize: '24px',
              color: 'rgba(0, 0, 0, 0.25)',
              background: 'transparent',
            },
          },
        });

        // Active traverse number — solid, from Gate_NG10 average, only if lap is kept
        if (traverseLap !== null && traverseLap > 0) {
          segmentLabelAnnotations.push({
            x: midpoint,
            strokeDashArray: 0,
            borderColor: 'transparent',
            label: {
              text: String(traverseLap),
              orientation: 'horizontal',
              position: 'top',
              offsetX: 14,
              offsetY: 8,
              style: {
                fontSize: '24px',
                color: 'rgba(0, 0, 0, 0.9)',
                background: 'transparent',
              },
            },
          });
        }
      });
    }

    // Create shaded regions for excluded segments
    const excludedRegionAnnotations = [];
    if (localExcludeTs && excludeCandidates && segmentTimestamps && localExcludeTs.length > 0) {
      // For each excluded timestamp value
      localExcludeTs.forEach((excludedValue) => {
        // Find the index of this value in exclude_candidates_ts
        const index = excludeCandidates.indexOf(excludedValue);
        if (index !== -1) {
          // This is the lap number (1-based) that is excluded
          const lapNumber = index + 1;

          // Determine the x-axis boundaries for this lap
          let x1, x2;

          if (lapNumber === 1) {
            // First lap: from start to first segment timestamp
            x1 = 0; // or use minTime if available
            x2 = segmentTimestamps[0];
          } else if (lapNumber <= segmentTimestamps.length) {
            // Middle laps: between consecutive segment timestamps
            x1 = segmentTimestamps[lapNumber - 2];
            x2 = segmentTimestamps[lapNumber - 1];
          } else if (lapNumber === segmentTimestamps.length + 1 && chartData && chartData.length > 0) {
            // Last lap: from last segment timestamp to end
            x1 = segmentTimestamps[segmentTimestamps.length - 1];
            x2 = globalMaxX;
          }

          if (x1 !== undefined && x2 !== undefined) {
            excludedRegionAnnotations.push({
              x: x1,
              x2: x2,
              fillColor: '#808080', // Grey color
              opacity: 0.1, // 10% opacity
            });
          }
        }
      });
    }

    // Calculate appropriate x-axis tick interval
    const calculateTickInterval = () => {
      if (!chartData || chartData.length === 0) return 60;

      const timeRange = globalMaxX - globalMinX;

      // Choose interval based on time range to get roughly 5-10 ticks
      if (timeRange <= 60) return 5; // 0-1 min: 5s intervals
      if (timeRange <= 300) return 10; // 1-5 min: 10s intervals
      if (timeRange <= 600) return 20; // 5-10 min: 20s intervals
      return 60; // >10 min: 60s intervals
    };

    const tickInterval = calculateTickInterval();

    // Combine line and label annotations
    const allXAxisAnnotations = [...xAxisAnnotations, ...segmentLabelAnnotations];
    return {
      chart: {
        type: 'line' as const,
        height: 400,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
          type: 'xy' as const, // Enable zooming on both x and y axes
          autoScaleYaxis: false, // Keep y-axis scale when zooming x-axis
        },
      },
      xaxis: {
        type: 'numeric' as const,
        title: {
          text: 'Time (seconds)',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: string) => `${parseFloat(value).toFixed(0)}s`,
        },
        tickAmount:
          chartData && chartData.length > 0 ? Math.floor((globalMaxX - globalMinX) / tickInterval) : 10,
        forceNiceScale: false,
        decimalsInFloat: 0,
        min: chartData && chartData.length > 0 ? Math.floor(globalMinX / tickInterval) * tickInterval : undefined,
        max: chartData && chartData.length > 0 ? Math.ceil(globalMaxX / tickInterval) * tickInterval : undefined,
      },
      yaxis,
      stroke: {
        curve: 'smooth' as const,
        width: 2,
      },
      colors: chartColors,
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 5,
      },
      tooltip: {
        x: {
          formatter: (value: number) => `${value.toFixed(2)} s`,
        },
        y: {
          formatter: function (value: number, { seriesIndex }: any) {
            const unit = seriesIndex < numPrimarySeries ? yAxisUnit : yAxisUnitRight || '';
            return `${value.toFixed(2)} ${unit}`;
          },
        },
      },
      title: {
        text: title,
        align: 'left' as const,
        style: {
          fontSize: '18px',
          fontWeight: 600,
        },
      },
      legend: {
        position: 'top' as const,
        horizontalAlign: 'left' as const,
      },
      annotations: {
        xaxis: [...allXAxisAnnotations, ...excludedRegionAnnotations],
      },
    };
  }, [
    chartData,
    title,
    availableFieldsSecondary,
    yAxisTitle,
    yAxisUnit,
    yAxisTitleRight,
    yAxisUnitRight,
    primarySeriesNames,
    secondarySeriesNames,
    chartColors,
    segmentTimestamps,
    localExcludeTs,
    excludeCandidates,
    data,
  ]);

  const showLapSelector = !hideLapSelector
    && ((excludeCandidates?.length ?? 0) > 0 || (segmentTimestamps?.length ?? 0) > 0);

  return (
    <>
      {!hideChipSelector && (
        <ChipSelector
          options={allAvailableFields}
          storageKey={storageKey}
          onSelectionChange={handleSelectionChange}
          defaultFields={defaultFields}
        />
      )}
      {chartData && chartData.length > 0 ? (
        <div className="cr-mt-4 cr-w-full">
          <ReactApexChart options={chartOptions} series={chartData || []} type="line" height={400} />

          {showLapSelector && (
            <div className="cr-mt-2 cr-border-t cr-border-gray-200">
              <button
                onClick={() => setIsLapSectionOpen(!isLapSectionOpen)}
                className="cr-flex cr-w-full cr-items-center cr-justify-start cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-text-gray-600 cr-transition-colors hover:cr-bg-gray-50 hover:cr-text-gray-900"
              >
                {isLapSectionOpen ? (
                  <HiChevronUp className="cr-mr-1 cr-h-5 cr-w-5" />
                ) : (
                  <HiChevronDown className="cr-mr-1 cr-h-5 cr-w-5" />
                )}
                Raw Lap Selection
              </button>

              {isLapSectionOpen && (
                <div className="cr-bg-gray-50 cr-p-4">
                  {lapNumbers.length > 0 ? (
                    <>
                      <div className="cr-overflow-x-auto">
                        <div className="cr-flex cr-min-w-fit cr-gap-4">
                          {lapNumbers.map((lapNumber) => (
                            <label key={lapNumber} className="cr-flex cr-cursor-pointer cr-flex-col cr-items-center">
                              <input
                                type="checkbox"
                                checked={selectedLaps[lapNumber] || false}
                                onChange={() => handleLapToggle(lapNumber)}
                                className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-blue-600 focus:cr-ring-blue-500"
                              />
                              <span className="cr-mt-1 cr-text-xs cr-text-gray-600">{lapNumber}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {runId && (
                        <div className="cr-mt-4 cr-flex cr-justify-start">
                          <button
                            onClick={handleSaveLapSelection}
                            disabled={isSaving}
                            className="cr-rounded cr-bg-blue-600 cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-text-white cr-transition-colors hover:cr-bg-blue-700 disabled:cr-cursor-not-allowed disabled:cr-opacity-50"
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="cr-text-sm cr-text-gray-500">No lap selection data available</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="cr-mt-4 cr-text-gray-500">
          No valid data available for plotting. Please select at least one data series above.
        </div>
      )}
    </>
  );
}
